import { api } from "./index";

export interface GameLoggerRecord {
    id: number;
    user_id: number | null;
    endpoint: string;
    method: string;
    status: string;
    error: Record<string, unknown> | null;
    created_at: string;
}

export interface GameLoggerFullRecord extends GameLoggerRecord {
    params: Record<string, unknown> | null;
    response: unknown;
    updated_at: string | null;
}

export interface GameLoggerListParams {
    user_id?: number;
    method?: string;
    status?: string;
    endpoint?: string;
    date_from?: string;
    date_to?: string;
    per_page?: number;
    page?: number;
    sort_by?: "id" | "created_at" | "user_id" | "method" | "status" | "endpoint";
    sort_order?: "asc" | "desc";
}

export interface GameLoggerListResponse {
    success: boolean;
    data: GameLoggerRecord[];
    meta: {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
    };
}

export interface GameLoggerStats {
    total: number;
    by_status: Record<string, number>;
    success_count: number;
    client_error_count: number;
    server_error_count: number;
    top_users: { user_id: number; count: number }[];
    top_endpoints: { endpoint: string; count: number }[];
}

export interface GameLoggerStatsResponse {
    success: boolean;
    data: GameLoggerStats;
}

export interface GameLoggerDetailResponse {
    success: boolean;
    data: GameLoggerFullRecord;
}

export const gameLoggerApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getGameLoggerList: builder.query<
            GameLoggerListResponse,
            GameLoggerListParams | void
        >({
            query: (params) => {
                if (!params) {
                    return "/admin/game-logger";
                }
                const searchParams = new URLSearchParams();
                if (params.user_id) searchParams.append("user_id", params.user_id.toString());
                if (params.method) searchParams.append("method", params.method);
                if (params.status) searchParams.append("status", params.status);
                if (params.endpoint) searchParams.append("endpoint", params.endpoint);
                if (params.date_from) searchParams.append("date_from", params.date_from);
                if (params.date_to) searchParams.append("date_to", params.date_to);
                if (params.per_page) searchParams.append("per_page", params.per_page.toString());
                if (params.page) searchParams.append("page", params.page.toString());
                if (params.sort_by) searchParams.append("sort_by", params.sort_by);
                if (params.sort_order) searchParams.append("sort_order", params.sort_order);
                const queryString = searchParams.toString();
                return `/admin/game-logger${queryString ? `?${queryString}` : ""}`;
            },
            providesTags: ["GameLogger"],
        }),
        getGameLoggerStats: builder.query<
            GameLoggerStatsResponse,
            { date_from?: string; date_to?: string } | void
        >({
            query: (params) => {
                if (!params) {
                    return "/admin/game-logger/stats";
                }
                const searchParams = new URLSearchParams();
                if (params.date_from) searchParams.append("date_from", params.date_from);
                if (params.date_to) searchParams.append("date_to", params.date_to);
                const queryString = searchParams.toString();
                return `/admin/game-logger/stats${queryString ? `?${queryString}` : ""}`;
            },
            providesTags: ["GameLogger"],
        }),
        getGameLoggerDetail: builder.query<
            GameLoggerDetailResponse,
            number
        >({
            query: (id) => `/admin/game-logger/${id}`,
            providesTags: (_result, _error, id) => [{ type: "GameLogger" as const, id }],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetGameLoggerListQuery,
    useGetGameLoggerStatsQuery,
    useGetGameLoggerDetailQuery,
    useLazyGetGameLoggerDetailQuery,
} = gameLoggerApi;
